import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureGet, secureDelete } from '../../config/api';
import { PrimaryButton } from '../../components/PrimaryButton';
import Input from '../../components/Input';

import Breadcrumbs from '../../components/Breadcrumbs';
import Filter from "../../components/Filter";
import VehicleRow from "../../components/VehicleRow";
import ModuleWrapper from '../../components/ModuleWrapper';
import DeletePopup from '../../components/DeletePopup';
import SnackbarNotification from "../../components/SnackbarNotification";
import UserContext from '../../Shared/UserContext';

import EditIcon from '../../icons/editB.svg';
import NoteIcon from '../../icons/messagesquare.svg';
import plusIcon from "../../icons/plus.svg";

const ClientDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isDeleteVehiclePopupVisible, setDeleteVehiclePopupVisible] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const { state: { role } } = useContext(UserContext);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const { t } = useTranslation();
  const { id } = useParams();
  const [client, setClient] = useState({
    name: '',
    code: '',
    cityName: '',
    address: '',
    taxId: '',
    contactPrincipal: '',
    phone: '',
    email: '',
    clientType: '',
    preferredCommunicationChannel: '',
    pieceReglementAutorisee: '',
    encoursMaximum: '',
    echeanceAutorisee: '',
  });
  const [filters, setFilters] = useState({ needle: "" });
  const [allVehicles, setAllVehicles] = useState([]);

  useEffect(() => {
    const abortController = new AbortController();
    const showSnackbar = () => { };
    const fetchClient = async () => {
      setLoading(true);
      try {
        const response = await secureGet(`/clients/${id}`, showSnackbar, {
          signal: abortController.signal
        });
        setClient(response.data);
        console.log(response.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch client:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClient();

    return () => {
      abortController.abort();
    };
  }, [id]);

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    }
  ];

  const handleFilterChange = (filterName, filterValue) => {
    const updated = { ...filters, [filterName]: filterValue };
    setFilters(updated);
  };

  const resetFilters = () => {
    const reset = { needle: "" };
    setFilters(reset);
  };


  const fetchVehicles = async () => {
    if (!client.id) {
      console.log("Client ID not available yet, skipping vehicle fetch");
      return;
    }

    try {
      const { data } = await secureGet(`/vehicles/client/${client.id}`, showSnackbar);
      setAllVehicles(data || []);
      console.log("Vehicles fetched:", data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setAllVehicles([]);
    }
  };

  // Filter vehicles locally based on needle
  const vehicles = useMemo(() => {
    if (!filters.needle) {
      return allVehicles;
    }
    const needleLower = filters.needle.toLowerCase();
    return allVehicles.filter(vehicle => {
      return (
        vehicle.immatriculationNumber?.toLowerCase().includes(needleLower) ||
        vehicle.brand?.name?.toLowerCase().includes(needleLower) ||
        vehicle.model?.toLowerCase().includes(needleLower) ||
        vehicle.color?.toLowerCase().includes(needleLower) ||
        vehicle.vinNumber?.toLowerCase().includes(needleLower)
      );
    });
  }, [allVehicles, filters.needle]);
  
  useEffect(() => {
    if (client.id) {
      fetchVehicles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const handleAddClick = () => {
    setSelectedVehicle(null);
    setShowModule(true);
  };

  const handleEditVehicleClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowModule(true);
  };

  const handleDeleteVehicleClick = (vehicle) => {
    setDeleteVehiclePopupVisible(true);
    setVehicleToDelete(vehicle);
  };

  const handleDeleteVehicleConfirm = async () => {
    if (!vehicleToDelete) return;
    
    try {
      if (vehicleToDelete.id) {
        await secureDelete(`/vehicles/${vehicleToDelete.id}`, showSnackbar);
        showSnackbar(t('confirmation.vehicleDelete'), 'success');
        fetchVehicles(); // Refresh the vehicle list
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
    } finally {
      setDeleteVehiclePopupVisible(false);
      setVehicleToDelete(null);
    }
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') {
      fetchVehicles();
    }
    setShowModule(false);
    setSelectedVehicle(null);
  };

  const { name, code, cityName, address, taxId, insuranceCompanyName, phone, email, clientType, preferredCommunicationChannel, pieceReglementAutorisee, encoursMaximum, echeanceAutorisee, insuranceCompany, intermediary } = client;

  const handleModifyClick = () => {
    history.push({
      pathname: `/clients/details/modify/${client.id}`,
      state: { initialValues: client, filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
    });
  };

  if (!client) {
    return <p>Client not found</p>;
  }

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Drivnest - {t("titles.client_details")}</title>
      </Helmet>
      <div className="flex flex-col mb-[20px]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Breadcrumbs />
            <h1 className="text-[32px] font-semibold">{t(`titles.client_details`)}</h1>
          </div>
          {(role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN") && (
            <div className="ml-auto text-[14px]">
              <PrimaryButton label={t(`primaryButtons.modify`)} icon={EditIcon} onClick={handleModifyClick} />
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2 ">
          <p className="text-[24px] font-semibold text-primary">
            {name}
          </p>
        </div>
      </div>



      <div className="">
        <div className="grid grid-cols-4 gap-4 lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 xs:grid-cols-1">
          <Input label={t(`clientDetails.name`)} value={name} onChange={() => { }} disabled={true} />
          <Input label={t(`clientDetails.code`)} value={code} onChange={() => { }} disabled={true} />
          <Input label={t(`clientDetails.city`)} value={cityName} onChange={() => { }} disabled={true} />
          <Input label={t(`clientDetails.address`)} value={address || ""} onChange={() => { }} disabled={true} />
          <Input label={t(`fields.clients.phone`)} value={phone || ''} onChange={() => { }} disabled={true} />
          <Input label={t(`fields.clients.email`)} value={email || ''} onChange={() => { }} disabled={true} />
          <Input label={t(`fields.clients.type`)} value={t(`client_types.${clientType}`) || ''} onChange={() => { }} disabled={true} />
          <Input label={t(`fields.clients.communicationChannel`)} value={t(`communicationChannels.${preferredCommunicationChannel}`) || ''} onChange={() => { }} disabled={true} />
          <Input 
            label={t(`fields.clients.paymentMode`)} 
            value={pieceReglementAutorisee ? (() => {
              const translationKey = `paymentModes.${pieceReglementAutorisee}`;
              const translated = t(translationKey);
              return translated !== translationKey ? translated : '';
            })() : ''} 
            onChange={() => { }} 
            disabled={true} 
          />
          {clientType === "COMPANY" && (
            <>
              <Input label={t(`fields.clients.encoursMaximum`)} value={encoursMaximum} onChange={() => { }} disabled={true} />
              <Input label={t(`fields.clients.echeanceAutorisee`)} value={echeanceAutorisee} onChange={() => { }} disabled={true} />
            </>
          )}
          {insuranceCompanyName && (
            <Input label={t(`fields.clients.insuranceCompany`)} value={insuranceCompanyName || ''} onChange={() => { }} disabled={true} />
          )}
        </div>
      </div>

      <div className="">
        <h3 className="text-[16px] font-semibold text-[#ACAFC6]"> {t(`fields_types.vehicleList`)}</h3>

        <div className="flex items-center gap-3">
          {/* Filters */}
          <div className="flex-1">
            <Filter
              filters={filterOptions}
              onFilterChange={handleFilterChange}
              resetFilters={resetFilters}
              filterValues={filters}
            />
          </div>

          {/* Add button */}
{/*           {(role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN") && (
            <button
              type="button"
              onClick={handleAddClick}
              className="bg-[#16283E]  w-[48px] h-[48px] rounded-[12px]  mt-7 flex items-center justify-center"
            >
              <img src={plusIcon} alt="Add" className="w-6 h-6" />
            </button>
          )} */}
        </div>
      </div>

      <div className="space-y-2">
        {vehicles.map((v) => (
          <VehicleRow
            key={v.id}
            vehicle={v}
            onDelete={handleDeleteVehicleClick}
            onEdit={handleEditVehicleClick}
          />
        ))}
      </div>

      {showModule && (
        <ModuleWrapper
          moduleType="vehicles"
          mode={selectedVehicle ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedVehicle || { clientId: client.id }}
        />
      )}

      {isDeleteVehiclePopupVisible && (
        <DeletePopup
          open={isDeleteVehiclePopupVisible}
          onCancel={() => setDeleteVehiclePopupVisible(false)}
          onConfirm={handleDeleteVehicleConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </div>
  );
};

export default ClientDetails;